import { motion } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../../i18n/config';

type Step = {
  number: string;
  title: string;
  description: string;
  details: string[];
};

export default function Process() {
  const [activeStep, setActiveStep] = useState<number>(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);
  const { t } = useTranslation();

  // Modify just the useEffect hook, leaving everything else exactly the same
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const stepNumber = Number(entry.target.getAttribute('data-step'));
            if (!isNaN(stepNumber)) {
              setActiveStep(stepNumber);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '-20% 0px -20% 0px', // Adjusted this value
        threshold: [0, 0.5, 1] // Added multiple thresholds
      }
    );

    // Make sure all steps are being observed
    stepsRef.current.forEach((step) => {
      if (step) {
        observer.observe(step);
      }
    });

    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, []);


  const steps: Step[] = [1, 2, 3].map(num => ({
    number: t(`process.steps.${num}.number`),
    title: t(`process.steps.${num}.title`),
    description: t(`process.steps.${num}.description`),
    details: t(`process.steps.${num}.details`, { returnObjects: true }) as string[]
  }));

  return (
    <section id="process" className="py-24 bg-background scroll-mt-24" ref={containerRef}>
      
      {/* Section Intro */}
      <div className="max-w-4xl mx-auto text-center mb-16 px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-secondary text-sm uppercase tracking-wider mb-2">
            {t('process.sectionTitle')}
          </h2>
          <h3 className="text-4xl lg:text-5xl font-bold text-primary mb-4">
            {t('process.title')}
          </h3>
          <p className="text-xl text-text/80">
            {t('process.description')}
          </p>
        </motion.div>
      </div>

      {/* Main Process Section */}
      <div className="relative">
        {/* Top Curve Decoration */}
        <div className="absolute top-0 left-0 right-0 h-8 bg-background">
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-primary rounded-t-[3rem]" />
        </div>

        {/* Content Container */}
        <div className="relative -mx-8 bg-primary rounded-t-[3rem] shadow-2xl"> {/* Added overflow-hidden */}

          <div className="grid grid-cols-12 gap-0 relative">
            {/* Left Panel */}
            <div className="col-span-5"> {/* Added explicit height to match scrolling area */}
              <div className="sticky top-[20vh] h-[60vh] flex flex-col justify-center px-12 lg:px-20">

                {/* Background Pattern */}
                <div 
                  className="absolute inset-0 opacity-[0.03]" 
                  style={{
                    backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                    backgroundSize: '24px 24px',
                  }}
                />
                
                <motion.div 
                  className="relative"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8 }}
                >
                  <h2 className="text-white/80 text-sm uppercase tracking-wider mb-2">Process</h2>
                  <h3 className="text-4xl lg:text-5xl text-white font-bold mb-8">{t('process.howItWorks')}</h3>
                  
                  {/* Step Numbers */}
                  <div className="space-y-4">
                    {steps.map((step, index) => (
                      <motion.div 
                        key={step.number}
                        className="flex items-center space-x-4"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                      >
                        <span 
                          className={`text-3xl font-bold transition-all duration-500 ${
                            activeStep === index + 1 
                              ? 'text-secondary scale-110' 
                              : 'text-white/40'
                          }`}
                        >
                          {step.number}
                        </span>
                        <div 
                          className={`h-[1px] w-12 transition-all duration-500 ${
                            activeStep === index + 1 
                              ? 'bg-secondary' 
                              : 'bg-white/20'
                          }`} 
                        />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Fixed Divider */}
            <div className="col-span-1 relative h-full">
              <div className="absolute top-16 -bottom-24 left-1/2 w-[2px] bg-white/5">
                {/* Main Gradient Line */}
                <div className="sticky top-[50vh] -translate-y-1/2">
                  {/* Outer Glow */}
                  <div className="absolute h-48 w-[1px] left-1/2 -translate-x-1/2 blur-md bg-gradient-to-b from-transparent via-secondary/50 to-transparent" />
                  
                  {/* Main Line */}
                  <div className="relative h-48">
                    <div className="absolute h-full w-[2px] left-1/2 -translate-x-1/2 bg-gradient-to-b from-transparent via-secondary/80 to-transparent" />
                    
                    <div className="absolute h-full w-[2px] left-1/2 -translate-x-1/2">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/40 to-transparent animate-pulse" />
                    </div>
                    
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 space-y-8">
                      <div className="w-2 h-2 rounded-full bg-secondary shadow-lg shadow-secondary/50" />
                      <div className="w-1 h-1 rounded-full bg-secondary/80" />
                      <div className="w-1 h-1 rounded-full bg-secondary/60" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel */}
            <div className="col-span-6">
              {steps.map((step, index) => (
                <div 
                  key={step.number}
                  ref={el => stepsRef.current[index] = el}
                  data-step={index + 1}
                  className="min-h-screen flex items-center px-6 sm:px-12 lg:px-20"
                >
                  <motion.div 
                    className="max-w-2xl pr-6 sm:pr-0"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-20%" }}
                    transition={{ duration: 0.6 }}
                  >
                    <h3 className="text-3xl lg:text-4xl font-bold text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-lg lg:text-xl text-white/80 mb-5 leading-relaxed">
                      {step.description}
                    </p>
                    <div className="space-y-2">
                      {step.details.map((detail, detailIndex) => (
                        <motion.div 
                          key={detail}
                          className="flex items-center space-x-3"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: 0.2 + detailIndex * 0.1 }}
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-secondary" />
                          <span className="text-sm lg:text-base text-white/70">{detail}</span>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
